import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Wind, Wifi, Tv, Phone, Bath, Users, BedDouble, Sofa } from "lucide-react";
import { listRoomsAndBlocks } from "@/lib/booking.functions";
import { computeEffectivePrice, type Season } from "@/lib/pricing";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/queen-bed-suite-crystal-beach")({
  head: () => ({
    meta: [
      { title: "Queen Bed Suite - Malak BnB Resort, Crystal Beach" },
      { name: "description", content: "Queen Bed Suite at Malak BnB Resort in Crystal Beach, Ontario. 440 sq ft with a living area, from C$380 a night, near Bay Beach and Lake Erie." },
      { property: "og:title", content: "Queen Bed Suite - Malak BnB Resort, Crystal Beach" },
      { property: "og:description", content: "The most spacious room at Malak BnB Resort, with two queen beds and a living area, five minutes from Bay Beach." },
      { property: "og:url", content: "https://malakbnbresort.ca/queen-bed-suite-crystal-beach" },
      { property: "og:image", content: "/images/room-queen-suite.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Rooms", path: "/rooms" },
          { name: "Queen Bed Suite", path: "/queen-bed-suite-crystal-beach" },
        ]),
      },
    ],
    links: [canonical("/queen-bed-suite-crystal-beach")],
  }),
  component: QueenBedSuitePage,
});

const AMENITIES = [
  { icon: Wind, label: "Air Conditioning" },
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Tv, label: "Flat Screen TV" },
  { icon: Sofa, label: "Living Area" },
  { icon: Phone, label: "In-Room Phone" },
  { icon: Bath, label: "Private Bathroom" },
];

const FAQ = [
  {
    q: "What makes the Queen Bed Suite different from the other rooms?",
    a: "The Queen Bed Suite at Malak BnB Resort is the largest room on the property at 440 sq ft, with two queen beds and its own living area. It suits guests who want extra space to spread out, whether that is a longer stay or a small group visiting Crystal Beach together.",
  },
  {
    q: "Is the Queen Bed Suite good for a longer stay?",
    a: "Yes. The separate living area at Malak BnB Resort gives guests room to work, relax, or spread out belongings that a standard room doesn't allow, which is why many guests booking a longer Crystal Beach stay request this suite specifically.",
  },
  {
    q: "How do I request the Queen Bed Suite at Malak BnB Resort?",
    a: "Submit a reservation request through the Malak BnB Resort booking form with the Queen Bed Suite selected and your preferred dates. The team personally confirms availability before sending payment details.",
  },
];

function QueenBedSuitePage() {
  const listFn = useServerFn(listRoomsAndBlocks);
  const { data } = useQuery({ queryKey: ["rooms-and-blocks"], queryFn: () => listFn() });
  const today = new Date();
  const dbRoom = data?.rooms.find((r) => r.name === "Queen Bed Suite");
  const price = dbRoom
    ? `C$${computeEffectivePrice(Number(dbRoom.price_cad), today, (data?.seasons ?? []) as Season[])}`
    : "C$380";

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] overflow-hidden">
        <img
          src="/images/room-queen-suite.webp"
          alt="Queen Bed Suite at Malak BnB Resort in Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/10 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/rooms" className="hover:text-white transition-colors">Rooms</Link>
              <span>/</span>
              <span>Queen Bed Suite</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight">
              Queen Bed Suite at Malak BnB Resort
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">Crystal Beach, Ontario</p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Two queen beds and a living area of your own, five minutes from Bay Beach.
          </p>
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              The Queen Bed Suite is the room to book when you want more than just a place to sleep. Two queen beds
              with fine bedding sit alongside a spacious living area, giving the suite a footprint that the other
              rooms at Malak BnB Resort simply don't have.
            </p>
            <p>
              At 440 sq ft, there is room to sit down with a coffee away from the bed, lay out a map of the Niagara
              Region for the next day's plans, or just let a longer stay feel less like living out of a suitcase.
              Hardwood floors, a flat screen TV, a dresser, and a phone finish the space.
            </p>
            <p>
              It is a favourite for guests staying more than a couple of nights, or small groups who want one room
              instead of two. Bay Beach is a five-minute walk, and breakfast is ready downstairs every morning.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Request This Room
            </Link>
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              See All Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* Details card */}
      <section className="pb-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="bg-card border border-border/50 rounded-2xl p-8 sm:p-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div className="flex items-start gap-3">
              <BedDouble className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Sleeping</p>
                <p className="text-foreground font-medium mt-1">2 Queen Beds</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Room Size</p>
                <p className="text-foreground font-medium mt-1">440 sq ft</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Rate</p>
              <p className="text-2xl font-heading text-foreground mt-1">From {price}</p>
              <p className="text-xs text-muted-foreground">per night, rates vary by season</p>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl font-heading text-foreground mb-10">What's In the Suite</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {AMENITIES.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-background border border-border/50">
                <a.icon className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium text-foreground text-center">{a.label}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8">
            Every stay at Malak BnB Resort also includes continental breakfast and access to the outdoor heated pool.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">About This Suite</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/meetings-and-events" className="text-primary font-semibold text-sm hover:underline">
            Planning a group visit? See Meetings and Events at Malak →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Ask about availability for the Queen Bed Suite
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request to Book
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
